import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { createUseStyles } from 'react-jss';
import { withTranslation } from 'react-i18next';

// material-ui icons
import { Edit, AddAlert, Add, Shield } from '@mui/icons-material';

// core components
import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Button from 'components/CustomButtons/Button.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SnackbarCustom from 'components/Snackbar/Snackbar.js';
import DeleteButton from 'components/Main/DeleteButton.js';
import SparaLoader from 'components/SparaLoader/SparaLoader';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import SparaFilter from 'components/SparaFilter/SparaFilter';

import stylesAlert from 'assets/jss/material-dashboard-pro-react/views/sweetAlertStyle.js';
import styles from 'assets/jss/material-dashboard-pro-react/views/extendedTablesStyle.js';
import formStyles from 'assets/jss/material-dashboard-pro-react/views/regularFormsStyle';

import { getRoles, createUpdateRole, deleteRole, getPermissions } from '../../store/actions/acl';
import AddRole from './addRole';

const style = {
    ...styles,
    ...stylesAlert,
    ...formStyles,
    stickyFilterContainer: {
        position: 'sticky',
        top: '50px',
        backgroundColor: 'var(--spara-chrome, white)',
        zIndex: 99,
        padding: '8px 16px 16px 16px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    },
    listHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '4px 8px 12px 8px',
    },
    listHeaderCount: {
        fontWeight: 'bold',
        fontSize: '16px',
        fontStyle: 'italic',
        color: '#007aaa',
    },
    rolesList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    roleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        padding: '10px 12px',
        borderRadius: '8px',
        border: '1px solid rgba(0,0,0,0.07)',
        backgroundColor: 'var(--spara-surface, #fff)',
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 122, 170, 0.1)',
            borderColor: 'rgba(0, 122, 170, 0.25)',
        },
    },
    roleIconBubble: {
        width: '36px',
        height: '36px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: '#2e7d32',
    },
    roleInfo: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
    },
    roleNameRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
    },
    roleName: {
        fontWeight: 600,
        fontSize: '0.875rem',
        color: 'var(--spara-text-strong, #2d3748)',
        margin: 0,
        letterSpacing: '0.01em',
    },
    roleIdBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '0.7rem',
        color: '#718096',
        backgroundColor: 'var(--spara-surface-2, #f7fafc)',
        border: '1px solid var(--spara-border, #e2e8f0)',
        borderRadius: '4px',
        padding: '1px 6px',
        fontFamily: 'monospace',
        flexShrink: 0,
    },
    permissionChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '4px',
    },
    permissionChip: {
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: '0.7rem',
        color: '#2e7d32',
        backgroundColor: '#f1f8e9',
        border: '1px solid #c5e1a5',
        borderRadius: '4px',
        padding: '1px 7px',
        fontWeight: 500,
    },
    roleActions: {
        display: 'flex',
        alignItems: 'center',
        gap: '2px',
        flexShrink: 0,
    },
    noResults: {
        textAlign: 'center',
        padding: '40px 32px',
        color: 'rgba(0,0,0,0.4)',
        fontSize: '15px',
    },
};
const useStyles = createUseStyles(style);

const RolesPage = ({ t }) => {
    const dispatch = useDispatch();
    const classes = useStyles();
    const { roles, loading } = useSelector((state) => state.acl);
    const { permissions, loadingPer } = useSelector((state) => state.acl);
    const { status, message, type } = useSelector((state) => state.acl.notification);
    const [alert, setAlert] = useState(null);
    const [search, setSearch] = useState('');
    const userPermission = JSON.parse(localStorage.getItem('user')).permission;
    const ROLE_UPDATE = userPermission.includes('role-update');
    const ROLE_DELETE = userPermission.includes('role-delete');

    useEffect(() => {
        dispatch(getRoles());
        dispatch(getPermissions());
    }, [dispatch]);

    const onSubmit = (data, update) => {
        dispatch(createUpdateRole({ update: update, ...data }));
    };

    const hideAlert = () => {
        setAlert(null);
    };

    const htmlAlert = (data = {}) => {
        setAlert(
            <AddRole
                classes={classes}
                data={data}
                permissionsList={permissions}
                onCancel={() => hideAlert()}
                onSubmit={(data, update) => onSubmit(data, update)}
            />
        );
    };

    const handleSearchChange = (event) => {
        setSearch(event.target.value || '');
    };

    const allRoles = roles?.results || [];

    const filteredRoles = useMemo(() => {
        if (!search) return allRoles;
        const searchLower = search.toLowerCase();
        return allRoles.filter(
            (r) =>
                r.name.toLowerCase().includes(searchLower) ||
                String(r.id).includes(searchLower)
        );
    }, [allRoles, search]);

    return (
        <GridContainer>
            {alert && createPortal(alert, document.body)}
            <SnackbarCustom
                place="br"
                icon={AddAlert}
                color={type === 'success' ? 'success' : 'warning'}
                message={message}
                open={status}
            />
            <GridItem xs={12}>
                <SparaTitle
                    title={t('menu.roles')}
                    rightComponent={
                        userPermission.includes('role-create') && (
                            <SparaButton
                                color="primary"
                                icon={Add}
                                onClick={() => htmlAlert()}
                            >
                                {t('acl.add_role')}
                            </SparaButton>
                        )
                    }
                />
                <Card style={{ margin: '0px' }}>
                    <div className={classes.stickyFilterContainer}>
                        <SparaFilter
                            filters={{}}
                            onFilterClick={() => {}}
                            onClearAll={() => setSearch('')}
                            search={search}
                            onSearchChange={handleSearchChange}
                            filterGroups={[]}
                            t={t}
                        />
                    </div>
                    <CardBody>
                        {loading || loadingPer ? (
                            <SparaLoader text={t('common.loading')} />
                        ) : filteredRoles.length === 0 ? (
                            <div className={classes.noResults}>
                                {t('no_results_with_current_filter')}
                            </div>
                        ) : (
                            <>
                                <div className={classes.listHeader}>
                                    <span className={classes.listHeaderCount}>
                                        {`${t('filter.showing')} ${filteredRoles.length} ${t('filter.of')} ${allRoles.length} ${t('menu.roles').toLowerCase()}`}
                                    </span>
                                </div>
                                <div className={classes.rolesList}>
                                    {filteredRoles.map((value) => (
                                        <div key={value.id} className={classes.roleRow}>
                                            <div className={classes.roleIconBubble}>
                                                <Shield style={{ fontSize: '17px' }} />
                                            </div>
                                            <div className={classes.roleInfo}>
                                                <div className={classes.roleNameRow}>
                                                    <p className={classes.roleName}>{value.name}</p>
                                                    <span className={classes.roleIdBadge}>
                                                        #{value.id}
                                                    </span>
                                                </div>
                                                {value.permissions?.length > 0 && (
                                                    <div className={classes.permissionChips}>
                                                        {value.permissions.map((per, i) => (
                                                            <span key={i} className={classes.permissionChip}>
                                                                {per.name}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div className={classes.roleActions}>
                                                {ROLE_UPDATE && (
                                                    <Button
                                                        color="success"
                                                        className={classes.actionButton}
                                                        onClick={() => htmlAlert(value)}
                                                        title={t('edit')}
                                                    >
                                                        <Edit className={classes.icon} />
                                                    </Button>
                                                )}
                                                {ROLE_DELETE && (
                                                    <DeleteButton
                                                        key={value.id + '_delete'}
                                                        action={deleteRole({ ...value })}
                                                        setLimit={() => {}}
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};

export default withTranslation('', { withRef: true })(RolesPage);
